/**
 * @file        src/pages/payment/GatewayList.jsx
 * @description Payment Management → Payment Gateways. READ-ONLY for a tenant
 *              admin: GET /api/payment-gateways never returns merchant_key/
 *              salt (only the has_custom_credentials flag), and the backend
 *              deliberately deletes the write handlers for this role — these
 *              rows carry the credentials that decide which account receives
 *              a customer's money, so every write now lives on the
 *              super-admin-only Platform → Tenant Management → edit tenant →
 *              Payment Gateways screen (AddTenant.jsx), which binds the
 *              target tenant's pool explicitly. This page exists so a tenant
 *              admin can still see which gateway is active per brand and in
 *              which mode, without any add/edit/delete affordance.
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-08-06 Claude — Re-wired into nav (was orphaned since the
 *                          07-27/08-03 multi-tenant rewrite moved all writes
 *                          to Platform and deleted this route's write
 *                          handlers). Dropped Add/Edit/Delete — there is no
 *                          write endpoint left to call — and repointed the
 *                          read query at the current /payment-gateways path.
 *   - 2026-08-03 Claude — Brand filter + labels come from the tenant's brand
 *                          registry (BrandSelect/useBrands), not a hardcoded map.
 *   - 2026-07-27 Claude — Brand column + filter (per-brand gateway selection).
 *   - 2026-07-03 Claude — Initial (Payment Management).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Chip from '../../components/Chip';
import BrandSelect from '../../components/BrandSelect';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';

export default function GatewayList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [brand, setBrand] = useState('');
  const { labelOf } = useBrands();

  const params = { page, pageSize };
  if (applied) params.q = applied;
  if (brand) params.brand = brand;

  const { data, isLoading } = useQuery({
    queryKey: qk.paymentGateways.list(params),
    queryFn: () => get('/payment-gateways', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Payment Gateways"
        subtitle="Payment Management · view only — edited by the platform operator per tenant"
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Gateway Name / ID</label>
            <input placeholder="Search gateway…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Brand</label>
            <BrandSelect
              value={brand}
              onChange={(v) => { setBrand(v); setPage(1); }}
              withAll
            />
          </div>
          <button className="btn primary" type="submit">Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Brand</th><th>Gateway Name</th><th>Display Name</th><th>Gateway ID</th><th>Convenience Fee</th><th>Credentials</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No gateways configured for this tenant yet.</td></tr>
                ) : rows.map((g, idx) => (
                  <tr key={g.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><Chip tone="ok">{labelOf(g.brand)}</Chip></td>
                    <td><b>{g.name}</b></td>
                    <td>{g.display_name || <span className="muted">—</span>}</td>
                    <td>{g.gateway_id || <span className="muted">—</span>}</td>
                    <td className="mono">{Number(g.convenience_fee || 0)} %</td>
                    <td>{g.has_custom_credentials ? <Chip tone="ok">Custom</Chip> : <span className="muted">Env default</span>}</td>
                    <td><Chip tone={g.is_active ? 'ok' : 'rej'}>{g.is_active ? 'Active' : 'Inactive'}</Chip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
